import React from 'react';
import { X } from 'lucide-react';

const PayslipDetail = ({ payslip, onClose }) => {
    if (!payslip) return null;

    // Filter out deductions that might be incorrectly categorized as earnings
    const deductionKeywords = ['tax', 'tds', 'income tax', 'deduction', 'pf', 'provident fund', 'esi', 'professional tax'];
    const isDeduction = (name) => {
        const lowerName = (name || '').toLowerCase();
        return deductionKeywords.some(keyword => lowerName.includes(keyword));
    };

    // Use backend-provided totals directly to avoid frontend calculation errors
    const monthlyGrossPay = payslip?.grossPay || 0;
    const monthlyTotalDeductions = payslip?.totalDeductions || 0;
    const monthlyNetPay = payslip?.netPay || 0;
    const actualPayThisMonth = payslip?.finalPay || 0; // The amount actually being paid in this batch

    // Categorize components for display purposes only
    const earnings = (payslip?.components?.earnings || []);
    const deductions = (payslip?.components?.deductions || []);
    
    // Check if salary structure exists for yearly display fallback
    const hasSalaryStructure = payslip?.salaryStructure?.ctc > 0;
    const annualGrossPay = hasSalaryStructure ? payslip.salaryStructure.ctc : (monthlyGrossPay * 12);
    const annualTotalDeductions = hasSalaryStructure ? (payslip.salaryStructure.ctc - payslip.salaryStructure.netPay) : (monthlyTotalDeductions * 12);
    const yearlyNetPay = hasSalaryStructure ? payslip.salaryStructure.netPay : (monthlyNetPay * 12);

    return (
        <div className="bg-white rounded-lg shadow-xl w-full h-full overflow-y-auto">
            <div className="p-6">
                <div className="flex items-center justify-between mb-6 gap-4">
                    <h2 className="text-2xl font-bold text-gray-900 break-words whitespace-normal word-break flex-1 min-w-0">Payslip - {payslip?.employeeName || 'N/A'}</h2>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    )}
                </div>

                {/* Payslip Header */}
                <div className="bg-gradient-to-r from-brand to-brand-dark rounded-lg p-6 text-white mb-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex-1 min-w-0">
                            <h3 className="text-xl font-bold break-words">{process.env.REACT_APP_COMPANY_NAME}</h3>
                            <p className="text-sm opacity-90 break-words">Payslip for {payslip?.period || 'N/A'}</p>
                        </div>
                        <div className="text-right flex-1 min-w-0">
                            <p className="text-sm opacity-90 break-words whitespace-normal">Employee ID: {payslip?.employeeId || 'N/A'}</p>
                            <p className="text-sm opacity-90 break-words whitespace-normal word-break break-all">Department: {payslip?.department || 'N/A'}</p>
                        </div>
                    </div>
                </div>

                {/* Employee Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Employee Information</h4>
                        <div className="space-y-2">
                            <div className="flex justify-between gap-2">
                                <span className="text-gray-600 flex-shrink-0">Name:</span>
                                <span className="font-medium break-words whitespace-normal word-break break-all text-right flex-1 min-w-0">{payslip?.employeeName || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between gap-2">
                                <span className="text-gray-600 flex-shrink-0">Employee ID:</span>
                                <span className="font-medium break-words text-right">{payslip?.employeeId || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between gap-2">
                                <span className="text-gray-600 flex-shrink-0">Department:</span>
                                <span className="font-medium break-words whitespace-normal word-break break-all text-right flex-1 min-w-0">{payslip?.department || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between gap-2">
                                <span className="text-gray-600 flex-shrink-0">Pay Period:</span>
                                <span className="font-medium break-words text-right">{payslip?.period || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Pay Summary</h4>
                        <div className="space-y-4">
                            {/* Actual Pay for This Month */}
                            <div className="pb-3 border-b-2 border-blue-300 bg-blue-50 rounded p-3">
                                <p className="text-xs font-semibold text-blue-800 mb-2 uppercase">Actual Pay for This Month</p>
                                <div className="space-y-2">
                                    <div className="flex justify-between gap-2">
                                        <span className="text-gray-700 flex-shrink-0 text-sm font-medium">Amount Payable:</span>
                                        <span className="font-bold text-[#0B2033] break-words text-right text-base">₹{actualPayThisMonth.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </div>
                                    {payslip?.presentDays !== undefined && payslip?.workingDays !== undefined && (
                                        <div className="text-xs text-gray-600 mt-1">
                                            Based on {payslip.presentDays} working days out of {payslip.workingDays} days
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Monthly Salary Structure */}
                            <div className="pb-2 border-b border-gray-300">
                                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase">Salary Breakdown (Monthly)</p>
                                <div className="space-y-2">
                                    <div className="flex justify-between gap-2">
                                        <span className="text-gray-600 flex-shrink-0 text-sm">Gross Pay:</span>
                                        <span className="font-medium text-green-600 break-words text-right text-sm">₹{monthlyGrossPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="flex justify-between gap-2">
                                        <span className="text-gray-600 flex-shrink-0 text-sm">Total Deductions:</span>
                                        <span className="font-medium text-red-600 break-words text-right text-sm">₹{monthlyTotalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="flex justify-between border-t pt-2 gap-2">
                                        <span className="text-gray-900 font-semibold flex-shrink-0 text-sm">Net Monthly Salary:</span>
                                        <span className="font-bold text-gray-900 break-words text-right text-sm">₹{(payslip?.totalMonthPay || monthlyNetPay).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </div>
                                    {payslip?.paidPreviously > 0 && (
                                        <div className="flex justify-between gap-2 italic">
                                            <span className="text-[#0F2942] flex-shrink-0 text-xs">Already Paid (Prev. Batches):</span>
                                            <span className="font-medium text-[#0F2942] break-words text-right text-xs">- ₹{payslip.paidPreviously.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between border-t border-blue-200 pt-2 gap-2 bg-blue-50 -mx-4 px-4">
                                        <span className="text-blue-900 font-bold flex-shrink-0 text-sm">Balance Payable (This Batch):</span>
                                        <span className="font-bold text-[#0B2033] break-words text-right text-sm">₹{actualPayThisMonth.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Yearly Salary Structure */}
                            <div className="pt-2">
                                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase">Salary Structure (Yearly)</p>
                                <div className="space-y-2">
                                    <div className="flex justify-between gap-2">
                                        <span className="text-gray-600 flex-shrink-0 text-sm">Gross Pay:</span>
                                        <span className="font-medium text-green-600 break-words text-right text-sm">₹{annualGrossPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="flex justify-between gap-2">
                                        <span className="text-gray-600 flex-shrink-0 text-sm">Total Deductions:</span>
                                        <span className="font-medium text-red-600 break-words text-right text-sm">₹{annualTotalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="flex justify-between border-t pt-2 gap-2">
                                        <span className="text-gray-900 font-semibold flex-shrink-0 text-sm">Net Pay:</span>
                                        <span className="font-bold text-[#0F2942] break-words text-right text-sm">₹{yearlyNetPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Earnings and Deductions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Earnings */}
                    <div className="bg-white border border-gray-200 rounded-lg">
                        <div className="p-4 border-b border-gray-200">
                            <h4 className="text-lg font-semibold text-gray-900">Earnings</h4>
                        </div>
                        <div className="p-4">
                            <div className="space-y-3">
                                {earnings.length > 0 ? (
                                    earnings.map((earning, index) => {
                                        // Use values directly from backend - already pro-rated if needed
                                        const displayAmount = earning.amount;
                                        return (
                                            <div key={index} className="flex justify-between items-center gap-2">
                                                <span className="text-gray-700 break-words flex-1">{earning.name || 'N/A'}</span>
                                                <span className="font-medium text-green-600 flex-shrink-0">₹{displayAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-gray-500 text-sm">No earnings listed</div>
                                )}
                            </div>
                        </div>
                        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            <span className="font-semibold text-gray-900">Total Earnings</span>
                            <span className="font-bold text-green-700">₹{monthlyGrossPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                    </div>

                    {/* Deductions */}
                    <div className="bg-white border border-gray-200 rounded-lg">
                        <div className="p-4 border-b border-gray-200">
                            <h4 className="text-lg font-semibold text-gray-900">Deductions</h4>
                        </div>
                        <div className="p-4">
                            <div className="space-y-3">
                                {deductions.length > 0 ? (
                                    deductions.map((deduction, index) => {
                                        // Use values directly from backend
                                        const displayAmount = deduction.amount;
                                        return (
                                            <div key={index} className="flex justify-between items-center gap-2">
                                                <span className="text-gray-700 break-words flex-1">{deduction.name || 'N/A'}</span>
                                                <span className="font-medium text-red-600 flex-shrink-0">₹{displayAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-gray-500 text-sm">No deductions listed</div>
                                )}
                            </div>
                        </div>
                        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            <span className="font-semibold text-gray-900">Total Deductions</span>
                            <span className="font-bold text-red-700">₹{monthlyTotalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PayslipDetail;
